import React, { useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';

const InspectDrawerChromeContext = React.createContext({
  headerActions: null,
});

/**
 * Optional chrome (e.g. pivot CTA) injected by FeedInspectProvider.
 * @returns {{ headerActions: React.ReactNode }}
 */
export function useInspectDrawerChrome() {
  return useContext(InspectDrawerChromeContext);
}

export function InspectDrawerChromeProvider({ headerActions = null, children }) {
  return (
    <InspectDrawerChromeContext.Provider value={{ headerActions }}>
      {children}
    </InspectDrawerChromeContext.Provider>
  );
}

/**
 * Inspection panel shell — right drawer on md+, bottom sheet under md.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: Function,
 *   ariaLabel: string,
 *   testId?: string,
 *   eyebrow?: React.ReactNode,
 *   title: string,
 *   subtitle?: React.ReactNode,
 *   sourceHref?: string,
 *   sourceLabel?: string,
 *   headerActions?: React.ReactNode,
 *   children: React.ReactNode,
 * }} props
 */
export default function InspectDrawerShell({
  isOpen,
  onClose,
  ariaLabel,
  testId = 'inspect-drawer',
  eyebrow = null,
  title,
  subtitle = null,
  sourceHref = '',
  sourceLabel = 'Open Original Source ↗',
  headerActions = null,
  children,
}) {
  const chrome = useInspectDrawerChrome();
  const resolvedHeaderActions = headerActions ?? chrome?.headerActions ?? null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120]" role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        aria-label="Close inspection panel"
        onClick={onClose}
      />
      <aside
        data-testid={testId}
        className="inspect-drawer-panel fixed z-50 flex flex-col border-slate-700 bg-slate-900 shadow-2xl
          inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl border-t p-4
          animate-in slide-in-from-bottom duration-300
          sm:p-5
          md:inset-y-0 md:right-0 md:left-auto md:bottom-auto md:max-h-none md:w-full md:max-w-[390px]
          md:rounded-none md:border-t-0 md:border-l md:p-5 md:animate-none
          lg:max-w-[390px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="inspect-drawer-sheet-handle mx-auto my-2 h-1 w-10 shrink-0 rounded-full bg-slate-600/70 md:hidden"
          aria-hidden
          data-testid="inspect-drawer-sheet-handle"
        />
        <header className="inspect-drawer-header sticky top-0 z-50 mb-3 flex shrink-0 flex-col gap-2 border-b border-slate-800 bg-slate-900/95 px-3 py-1.5 sm:mb-4">
          <div className="flex items-center justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              {eyebrow ? (
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-300">
                  {eyebrow}
                </p>
              ) : null}
              <h3
                className="mt-1 text-sm font-bold leading-snug text-slate-100 break-words [overflow-wrap:anywhere] line-clamp-3 sm:text-base sm:line-clamp-2"
                title={typeof title === 'string' ? title : undefined}
              >
                {title}
              </h3>
              {subtitle ? (
                <div className="inspect-drawer-subtitle mt-1 line-clamp-2 text-xs leading-relaxed text-slate-400 sm:text-sm">
                  {subtitle}
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <OpenLinkButton
                href={sourceHref}
                label={sourceLabel}
                compact
                testId="inspect-drawer-open-source"
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="inspect-drawer-close"
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-800 text-slate-300 transition hover:text-white"
              >
                ✕
              </button>
            </div>
          </div>
          {resolvedHeaderActions ? (
            <div
              className="flex flex-wrap items-center gap-2"
              data-testid="inspect-drawer-header-actions"
            >
              {resolvedHeaderActions}
            </div>
          ) : null}
        </header>
        <div className="inspect-drawer-body inspect-drawer-body-text min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 text-sm">
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

/**
 * Styled external source CTA.
 *
 * @param {{ href: string, label?: string, compact?: boolean, testId?: string }} props
 */
export function OpenLinkButton({
  href,
  label = 'Open Link ↗',
  compact = false,
  testId,
}) {
  const url = String(href || '').trim();
  if (!url || url === '#') return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={testId}
      className={
        compact
          ? 'inline-flex items-center gap-1 rounded-lg border border-cyan-500/40 bg-cyan-950/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-cyan-100 transition hover:border-cyan-400/70 hover:bg-cyan-900/50'
          : 'inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/40 bg-cyan-950/40 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-cyan-100 transition hover:border-cyan-400/70 hover:bg-cyan-900/50'
      }
    >
      {label}
    </a>
  );
}
